/* Mobile card for a movie feed item. Poster left, content right, always-visible action buttons. */

import { memo, useState } from "react";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Star, Clock } from "lucide-react";
import { ThumbnailPlaceholder } from "@/components/feed/ThumbnailPlaceholder";
import type { FeedItemData } from "@/components/feed/FeedItemRow";

interface MovieItemMobileCardProps {
  item: FeedItemData;
  onOpen?: () => void;
  onDelete?: () => void;
  onComplete?: () => void;
  onQueue?: () => void;
  onBacklog?: () => void;
}

export const MovieItemMobileCard = memo(function MovieItemMobileCard({ item, onOpen, onDelete, onComplete, onQueue, onBacklog }: MovieItemMobileCardProps) {
  const [isPending, setIsPending] = useState(false);

  const posterUrl = item.moviePoster ?? item.thumbnail;

  function runAction(action?: () => void) {
    if (!action) return;
    setIsPending(true);
    action();
  }

  if (isPending) return null;

  return (
    <div data-slot="feed-card" className="flex flex-col gap-2 px-4 py-3">
      {/* Poster + info row */}
      <div className="flex gap-3">
        <div
          className="shrink-0 w-[60px] h-[90px] cursor-pointer"
          onClick={() => onOpen?.()}
        >
          {posterUrl ? (
            <img
              src={posterUrl}
              alt={item.title}
              className="w-full h-full rounded-sm object-cover bg-muted"
            />
          ) : (
            <ThumbnailPlaceholder contentType="movie" className="rounded-sm" />
          )}
        </div>

        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          <p
            className="text-base font-medium leading-snug cursor-pointer active:opacity-70"
            onClick={() => onOpen?.()}
          >
            {item.sourceStarred && <Star className="inline size-3 fill-current text-primary mr-1 align-baseline" />}
            {item.title}
          </p>

          {item.movieDirector && (
            <span className="text-xs text-muted-foreground">
              {item.movieDirector}
              {item.movieYear ? ` (${item.movieYear})` : ""}
            </span>
          )}

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {item.movieRuntime && (
              <span className="inline-flex items-center gap-0.5">
                <Clock className="size-3" />
                {item.movieRuntime}
              </span>
            )}
            {item.movieRating && (
              <span className="inline-flex items-center gap-0.5">
                <Star className="size-3 fill-current text-yellow-500" />
                {item.movieRating}
              </span>
            )}
          </div>

          {item.movieGenres && (
            <span className="text-xs text-muted-foreground truncate">
              {item.movieGenres}
            </span>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 px-2"
          onClick={() => runAction(onComplete)}
        >
          <kbd className="opacity-60 mr-1">c</kbd> complete
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 px-2"
          onClick={() => runAction(onQueue)}
        >
          <kbd className="opacity-60 mr-1">q</kbd> queue
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 px-2"
          onClick={() => runAction(onBacklog)}
        >
          <kbd className="opacity-60 mr-1">b</kbd> backlog
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 px-2"
          onClick={() => runAction(onDelete)}
        >
          <kbd className="opacity-60 mr-1">x</kbd> dismiss
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs h-7 px-1.5"
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </div>
    </div>
  );
});
